import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/* Shared layout for the in-app legal pages (Terms, Privacy, Vendor Portal End User Agreement), linked from the
   signup consent step and the vendor portal's agreement screen. Public, no sign-in needed (see App.tsx). */

export const LEGAL_UPDATED = '14 October 2026';
export const LEGAL_CONTACT = 'info@fluxvell.com';

export interface LegalSection { id: string; heading: string; body: ReactNode }

const css = `
.lg-root{min-height:100vh;background:#F8FAFC;color:#0F172A;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
  -webkit-font-smoothing:antialiased;color-scheme:light;display:flex;flex-direction:column}
.lg-head{background:#fff;border-bottom:1px solid #E2E8F0;padding:16px 28px;display:flex;align-items:center;justify-content:space-between;gap:16px}
.lg-head img{height:34px;width:auto;display:block}
.lg-back{font-size:14px;color:#185FA5;text-decoration:none;font-weight:600}.lg-back:hover{text-decoration:underline}
.lg-wrap{flex:1;width:100%;max-width:1040px;margin:0 auto;padding:48px 24px 80px;display:grid;grid-template-columns:220px minmax(0,1fr);gap:48px;align-items:start}
.lg-toc{position:sticky;top:24px;font-size:13px}
.lg-toc b{display:block;font-size:11.5px;letter-spacing:.06em;text-transform:uppercase;color:#94A3B8;margin-bottom:10px}
.lg-toc a{display:block;color:#475569;text-decoration:none;padding:5px 0;line-height:1.35}.lg-toc a:hover{color:#185FA5}
.lg-doc{background:#fff;border:1px solid #E2E8F0;border-radius:14px;padding:40px 44px}
.lg-doc h1{margin:0 0 6px;font-size:30px;font-weight:700;letter-spacing:-.02em}
.lg-meta{font-size:13.5px;color:#64748B;margin-bottom:28px}
.lg-intro{font-size:15.5px;line-height:1.75;color:#334155;margin:0 0 8px}
.lg-doc section{scroll-margin-top:24px;padding-top:26px}
.lg-doc h2{font-size:18px;font-weight:700;margin:0 0 10px;color:#0F172A}
.lg-doc p,.lg-doc li{font-size:15px;line-height:1.75;color:#334155}
.lg-doc p{margin:0 0 12px}
.lg-doc ul{margin:0 0 12px;padding-left:20px}
.lg-doc li{margin-bottom:6px}
.lg-doc a{color:#185FA5;font-weight:500}
.lg-foot{border-top:1px solid #E2E8F0;background:#fff;padding:20px 28px;font-size:13px;color:#64748B;display:flex;justify-content:center;gap:18px;flex-wrap:wrap}
.lg-foot a{color:#475569;text-decoration:none}.lg-foot a:hover{color:#185FA5}
@media (max-width:860px){.lg-wrap{grid-template-columns:1fr;gap:20px;padding-top:28px}.lg-toc{position:static}.lg-doc{padding:28px 22px}}
`;

export default function LegalPage({ title, intro, sections, backTo = '/signup', backLabel = '← Back to sign up' }: {
  title: string; intro: ReactNode; sections: LegalSection[]; backTo?: string; backLabel?: string;
}) {
  return (
    <div className="lg-root">
      <style>{css}</style>
      <header className="lg-head">
        <Link to="/"><img src="/logo.png" alt="FluxWatch" /></Link>
        <Link to={backTo} className="lg-back">{backLabel}</Link>
      </header>

      <div className="lg-wrap">
        <nav className="lg-toc" aria-label="Contents">
          <b>Contents</b>
          {sections.map((s, i) => <a key={s.id} href={`#${s.id}`}>{i + 1}. {s.heading}</a>)}
        </nav>
        <article className="lg-doc">
          <h1>{title}</h1>
          <div className="lg-meta">Last updated {LEGAL_UPDATED}</div>
          <div className="lg-intro">{intro}</div>
          {sections.map((s, i) => (
            <section key={s.id} id={s.id}>
              <h2>{i + 1}. {s.heading}</h2>
              {s.body}
            </section>
          ))}
        </article>
      </div>

      <footer className="lg-foot">
        <span>&copy; {new Date().getFullYear()} FluxVell. All rights reserved.</span>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <a href={`mailto:${LEGAL_CONTACT}`}>{LEGAL_CONTACT}</a>
      </footer>
    </div>
  );
}
